// src/modules/units/tyres/views/retread-batch/retreadbatchreceive.js
//
// Orchestrator for receiving tyres back against a batch. Fetches the batch fresh,
// lists the OUTSTANDING lines (status OUT), and renders one line per tyre:
//   TYRE ID · [ Retreaded | Rejected ] · <Cost excl VAT | Reason>
// Retreaded is the default. Switching a row to Rejected swaps its Cost input for a
// required Reason input. Every outstanding tyre carries an outcome, so submitting
// receives the whole batch in one go (Retreaded -> back in stock; Rejected ->
// scrapped). Written off is not offered here for now but is still supported by the
// schema (see the view + retread-batch-core); re-adding its button needs no change
// here — this collects whatever outcome a row carries.
//
// receiveAgainstRetreadBatch moves tyres and emits the events, so the detail behind
// refetches. A retryable failure is resumed by replaying the exact request.

import { fetchRetreadBatch, receiveAgainstRetreadBatch, resumeRetreadOperation } from '../retread-batch.api.js';
import { RETREAD_LINE_STATUS, OUTCOME_NEEDS_REASON, RECOVERY } from '../../shared/retread-batch-core.js';
import { tyreErrorText } from '../../shared/retread-batch-vocab.js';
import { receiveHTML, receiveRowHTML, receiveInputHTML, receiveStateRowHTML, DEFAULT_OUTCOME, NOT_RECEIVED } from './retreadbatchreceive.view.js';
import { retreadProductFactsHTML } from './retreadbatchproduct.view.js';
import { todayInputValue, isoFromDateInput } from '../../shared/tyre-dates.js';

const lineStatus = (l) => l?.status ?? l?.lineStatus ?? null;
const idStr = (v) => (v === null || v === undefined ? '' : String(v));
const needsReason = (o) => OUTCOME_NEEDS_REASON.includes(o);

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}
function numOrOmit(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}
function strOrOmit(value) {
  const s = (value || '').trim();
  return s || undefined;
}

/**
 * @param {HTMLElement} viewEl
 * @param {object} ctx
 * @param {object} ctx.batch  the batch (needs retreadBatchId; reference for the title)
 * @param {() => void} [ctx.onClose]
 * @param {() => void} [ctx.onDone] a successful receive -> back to the (refetched) detail
 * @returns {() => void} teardown
 */
export function renderRetreadBatchReceive(viewEl, ctx = {}) {
  let disposed = false;
  const batchId = ctx.batch?.retreadBatchId;
  const reference = ctx.batch?.reference || '';

  const root = el(`<div class="tm-rb-receive-root">${receiveHTML({ reference, lines: [] })}</div>`);
  viewEl.replaceChildren(root);

  let rowsEl = root.querySelector('[data-receive-rows]');
  let msgEl = root.querySelector('[data-receive-msg]');
  let submitBtn = root.querySelector('[data-receive-submit]');
  let returnedEl = root.querySelector('[data-receive-returned]');

  let mode = 'submit';   // 'submit' | 'retry'
  let retryRes = null;

  function setMsg(text, kind) {
    if (!msgEl) return;
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = `tm-form-msg${kind ? ` tm-form-msg-${kind}` : ''}`;
  }
  function setBusy(on) {
    root.querySelectorAll('button, input').forEach((n) => { n.disabled = on; });
  }
  function setSubmitLabel(label, nextMode) { mode = nextMode; if (submitBtn) submitBtn.textContent = label; }

  function clearRowErrors() {
    root.querySelectorAll('[data-receive-row]').forEach((row) => {
      row.classList.remove('tm-rb-recv-bad');
      const e = row.querySelector('[data-receive-err]');
      if (e) e.remove();
    });
  }
  function markRow(id, text) {
    const row = root.querySelector(`[data-receive-row][data-tyre-id="${CSS.escape(id)}"]`);
    if (!row) return;
    row.classList.add('tm-rb-recv-bad');
    let e = row.querySelector('[data-receive-err]');
    if (!e) { e = document.createElement('div'); e.className = 'tm-rb-recv-err'; e.setAttribute('data-receive-err', ''); row.appendChild(e); }
    e.textContent = text;
  }

  async function load() {
    setMsg('');
    rowsEl.innerHTML = receiveStateRowHTML('Loading outstanding tyres…');
    const res = await fetchRetreadBatch(batchId);
    if (disposed) return;
    if (!res.success) { rowsEl.innerHTML = receiveStateRowHTML(res.message || 'Could not load the batch.'); return; }

    const batch = res.batch || {};

    const productEl = root.querySelector('[data-receive-product]');
    if (productEl) productEl.innerHTML = retreadProductFactsHTML(batch);

    const lines = (Array.isArray(res.lines) ? res.lines : (batch.lines || []))
      .filter((l) => lineStatus(l) === RETREAD_LINE_STATUS.OUT);

    if (!lines.length) {
      rowsEl.innerHTML = receiveStateRowHTML('Nothing outstanding to receive on this batch.');
      if (submitBtn) submitBtn.disabled = true;
      return;
    }
    rowsEl.innerHTML = lines.map((l) => receiveRowHTML(l)).join('');
    if (submitBtn) submitBtn.disabled = false;
    if (returnedEl && !returnedEl.value) returnedEl.value = todayInputValue();
  }

  // Switch a row's outcome: highlight the picked button and swap the trailing input
  // (Cost <-> Reason). Reading the choice back off row.dataset.outcome keeps the row
  // itself the single source of truth for collectItems.
  function selectOutcome(btn) {
    const row = btn.closest('[data-receive-row]');
    const outcome = btn.dataset.outcome;
    if (!row || !outcome || row.dataset.outcome === outcome) return;

    row.dataset.outcome = outcome;
    row.querySelectorAll('[data-receive-outcome-btn]').forEach((b) => {
      const on = b.dataset.outcome === outcome;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const slot = row.querySelector('[data-receive-input]');
    if (slot) slot.innerHTML = receiveInputHTML(outcome);

    row.classList.remove('tm-rb-recv-bad');
    const errEl = row.querySelector('[data-receive-err]');
    if (errEl) errEl.remove();
  }

  // Build the items payload; returns null (and marks rows) when invalid. A row marked
  // "Not received" is skipped — that tyre stays outstanding (partial receive). Every
  // other outstanding row carries a real outcome (Retreaded by default).
  function collectItems() {
    clearRowErrors();
    const rows = Array.from(root.querySelectorAll('[data-receive-row]'));
    const items = [];
    let missingReason = false;

    for (const row of rows) {
      const id = row.dataset.tyreId;
      const outcome = row.dataset.outcome || DEFAULT_OUTCOME;
      if (outcome === NOT_RECEIVED) continue;   // not in this delivery — leave it OUT
      const item = { externalTyreId: id, outcome };

      if (needsReason(outcome)) {
        const reason = strOrOmit(row.querySelector('[data-receive-field="rejectReason"]')?.value);
        if (!reason) { markRow(id, 'A reason is required.'); missingReason = true; continue; }
        item.rejectReason = reason;
      } else {
        const cost = numOrOmit(row.querySelector('[data-receive-field="cost"]')?.value);
        if (cost !== undefined) item.cost = cost;
      }
      items.push(item);
    }

    if (missingReason) { setMsg('Add a reason for each rejected tyre.', 'error'); return null; }
    if (!items.length) { setMsg('Mark at least one tyre as Retreaded or Rejected — everything is set to Not received.', 'error'); return null; }
    return items;
  }

  function handleResult(res) {
    if (disposed) return;
    setBusy(false);
    if (res.success) { if (typeof ctx.onDone === 'function') ctx.onDone(); return; }

    retryRes = null;
    if (res.status === 403) { setMsg('You need administrator access to receive this batch.', 'error'); setSubmitLabel('Receive tyres', 'submit'); return; }
    if (res.recovery === RECOVERY.WAIT || res.locked) { setMsg('The batch is busy with another operation — try again shortly.', 'error'); setSubmitLabel('Receive tyres', 'submit'); return; }

    const tyreErrors = Array.isArray(res.tyreErrors) ? res.tyreErrors : [];
    const invalid = Array.isArray(res.invalidTyreIds) ? res.invalidTyreIds : [];
    if (tyreErrors.length || invalid.length) {
      for (const te of tyreErrors) { const id = idStr(te.externalTyreId); if (id) markRow(id, tyreErrorText(te.reason)); }
      for (const id of invalid) markRow(idStr(id), tyreErrorText(null));
      setMsg('Some tyres couldn’t be received — see the flagged rows.', 'error');
      setSubmitLabel('Receive tyres', 'submit');
      return;
    }
    if (res.recovery === RECOVERY.RETRY && res._replay) {
      retryRes = res;
      setMsg(`${res.message || 'Receiving didn’t complete.'} You can retry safely.`, 'error');
      setSubmitLabel('Retry receive', 'retry');
      return;
    }
    setMsg(res.message || 'Could not receive the tyres.', 'error');
    setSubmitLabel('Receive tyres', 'submit');
  }

  async function submit() {
    const items = collectItems();
    if (!items) return;
    const payload = { items };
    const iso = isoFromDateInput(returnedEl?.value);
    if (iso) payload.returnedAt = iso;
    const note = strOrOmit(root.querySelector('[data-receive-note]')?.value);
    if (note) payload.deliveryNoteReference = note;

    setBusy(true);
    setMsg('Receiving…');
    handleResult(await receiveAgainstRetreadBatch(batchId, payload, { reason: 'receive' }));
  }

  async function resume() {
    if (!retryRes) return;
    setBusy(true);
    setMsg('Retrying…');
    handleResult(await resumeRetreadOperation(retryRes, { reason: 'receive' }));
  }

  root.addEventListener('click', (e) => {
    const outcomeBtn = e.target.closest('[data-receive-outcome-btn]');
    if (outcomeBtn) { selectOutcome(outcomeBtn); return; }
    if (e.target.closest('[data-receive-submit]')) { mode === 'retry' ? resume() : submit(); return; }
    if (e.target.closest('[data-receive-cancel]')) { if (typeof ctx.onClose === 'function') ctx.onClose(); return; }
  });

  load();
  return () => { disposed = true; };
}
